import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { uniqueId } from "lodash";
import * as Yup from "yup";
import { Box, Button, Grid, Typography, CircularProgress } from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import AddIcon from "@mui/icons-material/Add";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import {
  getOptionValue,
  planOfActionEnum,
  severityEnum,
  urgencyEnum,
} from "common/enums";
import { FIRE_EMERGENCY_CHIEF_FIRE_OFFICER } from "common/constant";
import { DocumentName } from "pages/UtilityConsumers/style";
import Tooltip from "Components/Common/Tooltip";

import {
  filterEmptyStrings,
  generateFormData,
  getImageUrl,
  isAuthorizedUser,
} from "utils";
import getFileExtension from "utils/getFileExtension";

import {
  useGetObserverTypeQuery,
  useLazyFireSafetyBuildingObservationQuery,
  usePostFireSafetyBuildingObservationMutation,
} from "apis/FireSafety/FireSafetyObservation/fireSafetyObservation";
import { useGetAllTenantsQuery } from "apis/FireSafety/FireSafetyTenantDetail";

import pdfImg from "assets/images/pdf.png";
import { UploadImage } from "./style";

const validFileFormats = ["jpg", "png", "jpeg"];

const otherObservationValue = "other";

const planOfActionMoreThanSevenDays = getOptionValue(
  "planOfActionEnum",
  "More Than Seven Days"
);

const FireSafetyBuildingObservation = ({
  applicationId,
  scheduleId,
  handleClose,
  t,
}) => {
  const { data: { data: observationTypeOptions } = {} } =
    useGetObserverTypeQuery();

  const [addBuildingObservation, { isLoading }] =
    usePostFireSafetyBuildingObservationMutation();

  const [
    getBuildingObservationDataById,
    {
      isLoading: isBuildingObservationDataLoading,
      isFetching: isBuildingObservationDataFetching,
      data: { data: buildingObservationData } = [],
    },
  ] = useLazyFireSafetyBuildingObservationQuery();

  const { data: { data: tenantOptions = [] } = {} } =
    useGetAllTenantsQuery(applicationId);

  const { roles } = useSelector((state) => state.Login);

  const ref = useRef();
  const [isAddBtnClicked, setIsAddBtnClicked] = useState(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);

  const updatedObservationTypeOptions = useMemo(
    () => [
      ...(observationTypeOptions?.map(({ name }) => ({
        label: name,
        value: name,
      })) ?? []),
      { label: "Other", value: otherObservationValue },
    ],
    [observationTypeOptions]
  );

  const initialValues = useMemo(
    () => ({
      buildingInformationId: applicationId ?? "",
      scheduleId: scheduleId ?? "",
      observations: buildingObservationData?.observations?.map((item) => ({
        ...item,
        tenantId: item.floorWiseTenant?.id ?? "",
      })) ?? [
        {
          id: 0,
          observationType: "",
          otherObservationType: "",
          tenantId: "",
          provision: "",
          observationPhotographs: [],
          recommendations: "",
          planOfAction: "",
          severity: "",
          urgency: "",
          fireSafetyUser: { roles },
        },
      ],
    }),
    [applicationId, scheduleId, buildingObservationData]
  );

  const submitBtnHandler = async (values, isDraft = false) => {
    try {
      const payload = {
        ...values,
        observations: values.observations.map((item) => ({
          id: item.id,
          observationType: item.otherObservationType || item.observationType,
          tenantId: item.tenantId,
          planOfAction: item.planOfAction,
          provision: item.provision,
          recommendations: item.recommendations,
          severity: item.severity,
          urgency: item.urgency,
          observationPhotographs: item.observationPhotographs.map(
            ({ id, file, photographUrl }) => ({
              id: file ? 0 : id,
              file: file ?? "",
              photographUrl: photographUrl ?? "",
            })
          ),
        })),
        isDraft,
      };

      const formData = new FormData();
      generateFormData(formData, filterEmptyStrings(payload));
      await addBuildingObservation(formData);
      if (isDraft) {
        setIsSaveButtonClicked(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    validationSchema: Yup.object().shape({
      observations: Yup.array().of(
        Yup.object({
          observationType: Yup.string()
            .nullable()
            .test("isObservationTypeRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.observationType
              ) {
                return this.createError({
                  message: "Observation Type is required",
                });
              }
              return true;
            }),
          planOfAction: Yup.string()
            .nullable()
            .test("isPlanOfActionRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.planOfAction
              ) {
                return this.createError({
                  message: "Plan of action is required",
                });
              }
              return true;
            }),
          tenantId: Yup.string()
            .nullable()
            .test("isTenantRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !(current.tenantId >= 0)
              ) {
                return this.createError({
                  message: "Tenant is required",
                });
              }
              return true;
            }),

          severity: Yup.string()
            .nullable()
            .test("isSeverityRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.severity
              ) {
                return this.createError({
                  message: "Severity is required",
                });
              }
              return true;
            }),
          urgency: Yup.string()
            .nullable()
            .test("isUrgencyRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.urgency &&
                current.planOfAction === planOfActionMoreThanSevenDays
              ) {
                return this.createError({
                  message: "Severity is required",
                });
              }
              return true;
            }),
          recommendations: Yup.string()
            .nullable()
            .test("isRecommendationsRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.recommendations
              ) {
                return this.createError({
                  message: "Recommendation is required",
                });
              }
              return true;
            }),
          provision: Yup.string()
            .nullable()
            .test("isDescriptionRequired", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                isAuthorizedUser(roles, [
                  ...current.fireSafetyUser.roles,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                ]) &&
                !current.provision
              ) {
                return this.createError({
                  message: "Provision is required",
                });
              }
              return true;
            }),

          otherObservationType: Yup.string().test(
            "isOtherObservationTypeSelectedOther",
            "",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (
                current.observationType === otherObservationValue &&
                !current.otherObservationType
              ) {
                return this.createError({
                  message: "Observation name is required",
                });
              }
              return true;
            }
          ),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        await submitBtnHandler(values, false);
        handleClose();
      } catch (err) {}
    },
  });

  const { values, setFieldValue, setFieldTouched, handleSubmit } = formik;
  const { observations } = values;
  const handleAddNewObservation = (existingObservation) => {
    const newObservation = [
      ...existingObservation,
      {
        id: 0,
        observationType: "",
        otherObservationType: "",
        tenantId: "",
        observationPhotographs: [],
        recommendations: "",
        planOfAction: "",
        provision: "",
        severity: "",
        urgency: "",
        fireSafetyUser: { roles },
      },
    ];

    setIsAddBtnClicked(true);
    setFieldValue("observations", newObservation);
    setFieldTouched(`observations[${newObservation.length - 1}]`, false, false);
  };

  const handleRemoveBuildingObservation = (index) =>
    setFieldValue(
      "observations",
      observations.filter((_, ind) => ind !== index)
    );

  const fileChangeHandler = (files, index) => {
    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error("Please upload image and size should be less than 10 mb");
    } else {
      setFieldValue(`observations.${index}.observationPhotographs`, [
        ...observations[index].observationPhotographs,
        ...files.map((file) => ({
          id: uniqueId("doc__"),
          url: URL.createObjectURL(file),
          name: file.name,
          file: file,
        })),
      ]);
    }
  };

  const saveBtnHandler = () => {
    setIsSaveButtonClicked(true);
    submitBtnHandler(values, true);
  };

  useEffect(() => {
    (async () => {
      try {
        if (scheduleId) {
          await getBuildingObservationDataById(scheduleId);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [scheduleId]);

  // When new observation added it scrolls to bottom to that new observation
  useEffect(() => {
    if (observations && isAddBtnClicked) {
      ref.current?.scrollIntoView();
      setIsAddBtnClicked(false);
    }
  }, [observations]);

  return (
    <>
      <Backdrop
        open={
          isBuildingObservationDataFetching || isBuildingObservationDataLoading
        }
      />
      <Grid
        item
        xs={12}
        className="border ps-3 pe-2 pb-2 pt-0 mt-2"
        sx={{
          mb: 3,
          maxHeight: "594px",
          overflow: "auto",
        }}
      >
        {!!observations.length &&
          observations.map(
            (
              {
                planOfAction,
                observationPhotographs,
                observationType,
                isApproved,
                fireSafetyUser: {
                  roles: currentRoles = [],
                  firstName,
                  lastName,
                } = {},
              },
              index
            ) => {
              const isDisabled =
                ((!isAuthorizedUser(roles, currentRoles) ||
                  !currentRoles.length) &&
                  !roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER)) ||
                isApproved;

              return (
                <Grid
                  key={index}
                  container
                  columnSpacing={1}
                  rowSpacing={1}
                  className="border p-2 pe-3 mt-2"
                  sx={{ overflow: "auto" }}
                  ref={observations.length - 1 === index ? ref : null}
                >
                  {!!firstName && (
                    <Grid xs={12} className="ms-2 mb-1">
                      <Typography>
                        <b>{t("Observation Added By")}</b> : {firstName}{" "}
                        {lastName}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Observation Type") + "*"}
                      name={`observations.${index}.observationType`}
                      formik={formik}
                      options={updatedObservationTypeOptions}
                      disabled={isDisabled}
                      onChange={(_, { value }) => {
                        setFieldValue(
                          `observations.${index}.observationType`,
                          value
                        );
                        setFieldValue(
                          `observations.${index}.otherObservationType`,
                          ""
                        );
                      }}
                    />
                  </Grid>

                  {observationType === otherObservationValue && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <InputFieldText
                        label={t("Enter Observation Name") + "*"}
                        name={`observations.${index}.otherObservationType`}
                        formik={formik}
                        disabled={isDisabled}
                        placeholder="Enter Observation Name"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Select Tenant") + "*"}
                      name={`observations.${index}.tenantId`}
                      disableClearable={false}
                      disabled={isDisabled}
                      onChange={(_, option) => {
                        setFieldValue(
                          `observations.${index}.tenantId`,
                          option?.value ?? ""
                        );
                      }}
                      formik={formik}
                      options={tenantOptions}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Plan Of Action") + "*"}
                      name={`observations.${index}.planOfAction`}
                      disabled={isDisabled}
                      onChange={(_, { value }) => {
                        setFieldValue(
                          `observations.${index}.planOfAction`,
                          value
                        );
                        setFieldValue(`observations.${index}.urgency`, "");
                      }}
                      formik={formik}
                      options={planOfActionEnum}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Select Severity") + "*"}
                      name={`observations.${index}.severity`}
                      disabled={isDisabled}
                      formik={formik}
                      options={severityEnum}
                    />
                  </Grid>

                  {planOfAction === planOfActionMoreThanSevenDays && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <SelectField
                        label={t("Select Urgency") + "*"}
                        name={`observations.${index}.urgency`}
                        disabled={isDisabled}
                        formik={formik}
                        options={urgencyEnum}
                      />
                    </Grid>
                  )}

                  <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={2}
                    sx={{ pl: 1 }}
                  >
                    <Grid item xs={12} className="mb-3 ms-1 mt-1">
                      <div className="d-flex gap-3 flex-wrap">
                        {!!observationPhotographs.length &&
                          observationPhotographs.map(
                            (
                              { photographUrl, url, name, id: currentId },
                              inx
                            ) => (
                              <div className="image-container" key={inx}>
                                <a
                                  href={
                                    photographUrl
                                      ? getImageUrl(photographUrl)
                                      : url
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Box
                                    component="img"
                                    className="image d-block m-auto"
                                    alt="document image"
                                    src={
                                      getFileExtension(
                                        (photographUrl
                                          ? photographUrl
                                          : name
                                        ).toLowerCase()
                                      ) === "pdf"
                                        ? pdfImg
                                        : url ?? getImageUrl(photographUrl)
                                    }
                                  />
                                </a>
                                {!isDisabled && (
                                  <i
                                    className="ri-close-circle-line fs-3 close-btn"
                                    onClick={() => {
                                      setFieldValue(
                                        `observations.${index}.observationPhotographs`,
                                        observationPhotographs.filter(
                                          ({ id }) => id !== currentId
                                        )
                                      );
                                    }}
                                  />
                                )}
                                <Tooltip title={name}>
                                  <DocumentName fontSize={13} variant="body2">
                                    {name}
                                  </DocumentName>
                                </Tooltip>
                              </div>
                            )
                          )}

                        <Box
                          component="label"
                          className="m-0 p-0 cursor-pointer"
                        >
                          <UploadImage isDisabled={isDisabled}>
                            <AddIcon
                              sx={{
                                color: !isDisabled ? "black" : "#dddddd",
                              }}
                            />
                          </UploadImage>
                          <input
                            type="file"
                            accept="image pdf/*"
                            disabled={isDisabled}
                            onChange={(e) => {
                              fileChangeHandler(
                                Array.from(e.target.files),
                                index
                              );
                              e.target.value = null;
                            }}
                            multiple
                            hidden
                          />
                        </Box>
                      </div>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <InputFieldText
                      label={t("Provision") + "*"}
                      name={`observations.${index}.provision`}
                      disabled={isDisabled}
                      formik={formik}
                      placeholder="Enter Provision"
                      multiline
                      rows={2}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <InputFieldText
                      label={t("Recommendation") + "*"}
                      name={`observations.${index}.recommendations`}
                      disabled={isDisabled}
                      formik={formik}
                      placeholder="Enter Recommendations"
                      multiline
                      rows={2}
                    />
                  </Grid>

                  {!isDisabled && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Button
                        color="error"
                        size="small"
                        sx={{
                          minWidth: "unset",
                          border: "1px solid",
                          height: "40px",
                          width: "100px",
                          mb: 2,
                        }}
                        onClick={() => handleRemoveBuildingObservation(index)}
                      >
                        <strong className="d-flex justify-content-center">
                          <DeleteOutlineSharpIcon fontSize="small" />
                          {t("Remove")}
                        </strong>
                      </Button>
                    </Grid>
                  )}
                </Grid>
              );
            }
          )}
      </Grid>

      <Box className="d-flex justify-content-between flex-wrap gap-2">
        <Button
          color="primary"
          sx={{
            minWidth: "unset",
            textTransform: "capitalize",
          }}
          variant="outlined"
          onClick={() => handleAddNewObservation(observations)}
        >
          <strong>
            <span>+</span> {t("Add Observation")}
          </strong>
        </Button>

        <div className="d-flex align-items-center gap-1">
          <Button
            size="medium"
            variant="outlined"
            type="submit"
            onClick={saveBtnHandler}
            disabled={isLoading && isSaveButtonClicked}
          >
            {t("Save")}
            {isLoading && isSaveButtonClicked && <CircularProgress size={20} />}
          </Button>

          <Button
            size="medium"
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading && !isSaveButtonClicked}
          >
            {t("Submit")}
            {isLoading && !isSaveButtonClicked && (
              <CircularProgress size={20} />
            )}
          </Button>
        </div>
      </Box>
    </>
  );
};

FireSafetyBuildingObservation.propTypes = {
  t: PropTypes.func,
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  scheduleId: PropTypes.number,
};

export default withTranslation()(FireSafetyBuildingObservation);
